import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const CustomerDetailsForm = ({ onComplete, initialData = null, isCheckout = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/users/profile');
        if (response.data) {
          // Map the API response to match our form field names
          const userData = response.data;
          setFormData({
            firstName: userData.first_name || userData.firstName || '',
            lastName: userData.last_name || userData.lastName || '',
            email: userData.email || '',
            address: userData.address || userData.shipping_address || '',
            city: userData.city || userData.shipping_city || '',
            state: userData.state || userData.shipping_state || '',
            zipCode: userData.zip_code || userData.shipping_zip_code || userData.zipCode || '',
            country: userData.country || userData.shipping_country || '',
            phone: userData.phone || userData.customer_phone || '',
          });
        }
      } catch (err) {
        console.error('Error fetching customer details:', err);
        if (err.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    if (!initialData) {
      fetchCustomerDetails();
    } else {
      // Ensure all fields are present when using initialData
      setFormData(prev => ({
        firstName: initialData.firstName || initialData.first_name || '',
        lastName: initialData.lastName || initialData.last_name || '',
        email: initialData.email || '',
        address: initialData.address || initialData.shipping_address || '',
        city: initialData.city || initialData.shipping_city || '',
        state: initialData.state || initialData.shipping_state || '',
        zipCode: initialData.zipCode || initialData.zip_code || initialData.shipping_zip_code || '',
        country: initialData.country || initialData.shipping_country || '',
        phone: initialData.phone || initialData.customer_phone || '',
      }));
    }
  }, [initialData, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Format the data to match the API's expected structure
      const formattedData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        country: formData.country,
        phone: formData.phone,
      };

      await api.put('/api/users/profile', formattedData);
      
      if (onComplete) {
        onComplete(formData);
      } else {
        navigate('/profile');
      }
    } catch (err) {
      console.error('Error saving customer details:', err);
      setError(err.response?.data?.message || 'Failed to save details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="City"
            name="city"
            value={formData.city}
            onChange={handleChange}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="State/Province/Region"
            name="state"
            value={formData.state}
            onChange={handleChange}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="ZIP / Postal Code"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Phone Number"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            margin="normal"
            placeholder="123-456-7890"
            inputProps={{ pattern: '[0-9]{3}-[0-9]{3}-[0-9]{4}' }}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            sx={{ mt: 2, mb: 2 }}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Save Details'}
          </Button>
          {!isCheckout && (
            <Button
              variant="outlined"
              sx={{ mt: 2, mb: 2, ml: 2 }}
              onClick={() => navigate(0)}
            >
              Cancel
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerDetailsForm;
